import { BaseSelectOption } from '@/forms/types';

/**
 * Configuration object for Typesense collections with their corresponding token keys
 */
export interface TypesenseCollectionConfig {
  name: string;
  keyName: string;
}

/**
 * Typesense collection configurations with collection names and token keys
 */
export const TypesenseCollection = {
  CUSTOMERS_VIEW: {
    name: 'customers',
    keyName: 'customers_view',
  },
  SUPPLIERS_VIEW: {
    name: 'customers',
    keyName: 'suppliers_view',
  },
  PROJECTS_VIEW: {
    name: 'projects',
    keyName: 'projects_view',
  },
  CURRENCY_CODES: {
    name: 'currencycodes',
    keyName: 'currencycodes',
  },
  CUSTOMERS: {
    name: 'customers',
    keyName: 'customers',
  },
  PROJECTS: {
    name: 'projects',
    keyName: 'projects',
  },
  EMPLOYEES: {
    name: 'employees',
    keyName: 'employees',
  },
  EMPLOYEES_VIEW: {
    name: 'employees',
    keyName: 'employees_view',
  },
  EXPENSE_TYPES: {
    name: 'expensetypes',
    keyName: 'expensetypes',
  },
  INVOICES: {
    name: 'invoices',
    keyName: 'invoices',
  },
  CONTACTS: {
    name: 'contacts',
    keyName: 'contacts',
  },
  ACTIONS: {
    name: 'actions',
    keyName: 'actions',
  },
  PAYMENT_METHODS: {
    name: 'paymentmethods',
    keyName: 'paymentmethods',
  },
  PROJECT_TYPES: {
    name: 'projecttypes',
    keyName: 'projecttypes',
  },
  PROJECT_CATEGORIES: {
    name: 'projectcategories',
    keyName: 'projectcategories',
  },
  PROJECT_SUBCONTRACTS: {
    name: 'projectsubcontracts',
    keyName: 'projectsubcontracts',
  },
  EMPLOYEE_TYPES: {
    name: 'employeetypes',
    keyName: 'employeetypes',
  },
  MANAGERS: {
    name: 'employees',
    keyName: 'managers',
  },
  DASHBOARDS: {
    name: 'dashboards',
    keyName: 'dashboards',
  },
} as const satisfies Record<string, TypesenseCollectionConfig>;

/**
 * Type for collection names - provides string literal union type
 */
export type TypesenseCollectionName =
  (typeof TypesenseCollection)[keyof typeof TypesenseCollection]['name'];

/**
 * Type for token keys - provides string literal union type
 */
export type TypesenseKeyName =
  (typeof TypesenseCollection)[keyof typeof TypesenseCollection]['keyName'];

/**
 * A pair of a Typesense collection name and its resolved API key, used for union multi-search
 */
export type TypesenseCollectionApiKeyPair = {
  collectionName: TypesenseCollectionName;
  keyName: TypesenseKeyName;
  apiKey: string;
};

/**
 * Search function options to filter and sort select option results
 */
export interface SearchOptions {
  filterBy?: string;
  sortBy?: string;
}

/**
 * Search options for union multi-search.
 * sortBy is shared across all collections (required by Typesense union search).
 * filterBy is per-collection, keyed by collection keyName — use TypesenseCollection.X.keyName.
 */
export type UnionSearchOptions = {
  sortBy?: string;
  filterBy?: Partial<Record<TypesenseKeyName, string>>;
};

/**
 * Generic select option search function type definition
 */
export type SelectOptionSearchFunction<
  T extends BaseSelectOption = BaseSelectOption,
> = (
  searchValue: string,
  apiKey: string,
  pageIndex?: number,
  options?: SearchOptions,
) => Promise<T[]>;

/**
 * Generic function type definition to get a specific select option by its ID (getById behavior)
 */
export type GetSelectOptionByIdFunction<
  T extends BaseSelectOption = BaseSelectOption,
> = (
  id: string,
  apiKey: string,
  additionalFilterOptions?: SearchOptions['filterBy'],
) => Promise<T>;

/**
 * Generic select option search function type definition
 */
export type SelectOptionFromUnionSearchFunction<
  T extends BaseSelectOption = BaseSelectOption,
> = (
  searchValue: string,
  apiKeys: TypesenseCollectionApiKeyPair[],
  pageIndex?: number,
  options?: SearchOptions | UnionSearchOptions,
) => Promise<T[]>;

/**
 * Generic function type definition to get a specific select option by its ID (getById behavior) from a unionized multi-search result
 */
export type GetSelectOptionByIdFromUnionFunction<
  T extends BaseSelectOption = BaseSelectOption,
> = (
  id: string,
  apiKeys: TypesenseCollectionApiKeyPair[],
  additionalFilterOptions?: SearchOptions | UnionSearchOptions,
) => Promise<T>;

/**
 * Typesense highlight information for a matched field.
 * Contains the matched tokens and a snippet with <mark> tags around matches.
 */
export interface TypesenseHighlightField {
  matched_tokens: string[];
  snippet: string;
}

/**
 * Typesense highlight object containing matched fields.
 * Keys are field names that matched the search query.
 * This is used for the `highlight` property (singular).
 */
export type TypesenseHighlight = Record<string, TypesenseHighlightField>;

/**
 * Typesense flat highlight item from the highlights array.
 * Contains a single matched field with its snippet and matched tokens.
 * This is used for the `highlights` property (plural, array).
 */
export interface TypesenseHighlightItem {
  field: string;
  matched_tokens: string[];
  snippet: string;
  snippets?: string[];
}
